import {
  Badge,
  Banner,
  Button,
  ButtonLink,
  ConfirmDialog,
  DataTable,
  Drawer,
  EmptyState,
  Hint,
  Metric,
  MiniBar,
  Panel,
  Row,
  Segmented,
  Select,
  Spinner,
  StatBar,
  TextArea,
  TextField,
  Toolbar,
  type Column,
  type SelectOption,
  type StatSegment,
  type Tone,
} from '@ui';
import { createContext, FormEvent, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { ApiError, attachmentUrl } from './api';
import { getBases } from './base';
import {
  BUG_STATUS_LABELS,
  bugApi,
  reportLabel,
  type BugProfileField,
  type BugProfileKey,
  type BugPrompt,
  type BugReportDetail,
  type BugSettings,
  type BugStatus,
  type BugToken,
  type IssuedPassword,
  type IssuedProjectKey,
  type IssuedProjectToken,
  type RegistryProject,
  type StoredQuestion,
  type TrackerUsers as TrackerUserList,
} from './bugApi';
import { settingsConfig } from './config';
import { BugIcon, ChevronIcon, PlusIcon } from './icons';
import { CopyValue, RowMenu, useConfirm, type ConfirmAsk } from './jui';
import { HeaderActions } from './tracker/HeaderActions';
import { allowedMove } from '../../shared/board';
import EpicList, { ReportEpics } from './board/EpicBoard';
import EpicKanban from './board/EpicKanban';
import { AgentControlBlock } from './board/AgentControlBlock';
import { RepeatBlock } from './board/RepeatBlock';
import ReportBoard from './board/ReportBoard';
import { AllReports, BacklogPage } from './board/ReportPages';
import { formatDateTime } from './labels';
import { MAX_WORD, hintFit, hintHead, glossaryWords, longWord } from '../../shared/voice';

function errorOf(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/** Same cap as the service keeps for a prompt's user part. */
const MAX_PROMPT = 20_000;

/** One line of the shot tool's log (`clientState.shotLog`, reports 105–107), as the card shows it. */
interface ShotLogLine {
  kind: string;
  page: string;
  masked?: boolean;
  area?: { w: number; h: number };
  ms?: number;
  px?: { w: number; h: number };
  /** Blocks of the screen (mark and path) in the frame — the window, for a shot of the page (Quint shot_blocks). */
  blocks?: { block: string; path: string }[];
  result: string;
  error?: string;
}
const SHOT_KINDS: Record<string, string> = {
  open: 'при открытии',
  button: 'кнопка',
  area: 'рамка',
  file: 'с диска',
  paste: 'вставка',
  drop: 'перетащили',
  restore: '«Вернуть»',
  retake: 'переснят',
};
const SHOT_RESULTS: Record<string, string> = {
  ready: 'готов',
  empty: 'в области ничего нет',
  failed: 'не вышел',
};
const SHOT_TONES: Record<string, Tone> = {
  ready: 'success',
  empty: 'warning',
  failed: 'error',
};

function shotLogOf(state: unknown): ShotLogLine[] {
  const log = state && typeof state === 'object' ? (state as { shotLog?: unknown }).shotLog : null;
  if (!Array.isArray(log)) return [];
  return log.filter((entry): entry is ShotLogLine => !!entry && typeof entry === 'object' && typeof (entry as ShotLogLine).kind === 'string');
}
const USER_PART_MARK = '⟨пользовательская часть⟩';

/** One confirmation dialog for the whole screen (facts model widget_ui, П307): a second ask while it is open is «нет». */
const ConfirmContext = createContext<(ask: ConfirmAsk) => Promise<boolean>>(() => Promise.resolve(false));
const useAsk = () => useContext(ConfirmContext);

const STATUS_TONES: Record<BugStatus, Tone> = {
  backlog: 'error',
  requirements: 'warning',
  validation: 'info',
  implementation: 'accent',
  deploy: 'accent',
  fixed: 'success',
  wont_fix: 'neutral',
  duplicate: 'neutral',
  closed: 'neutral',
};
const ALL_STATUSES = Object.keys(BUG_STATUS_LABELS) as BugStatus[];
const STATUS_OPTIONS: SelectOption[] = ALL_STATUSES.map((key) => ({
  value: key,
  label: BUG_STATUS_LABELS[key],
}));

function StatusBadge({ status }: { status: BugStatus }) {
  return <Badge tone={STATUS_TONES[status]}>{BUG_STATUS_LABELS[status]}</Badge>;
}

const Loading = () => <Spinner label="Загрузка" size="sm" />;

const dayOf = (iso: string): string => new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
/** Т5: the tracker site shows one section per tab; a host (`mountSettings`) keeps the whole screen (`all`). */
/** `backlog`, `board`, `epics`, `allReports` — the tracker site's report sections (Пт3–Пт5); `reports` — the host's one screen. */
export type SettingsSection = 'all' | 'reports' | 'backlog' | 'board' | 'taskList' | 'epics' | 'epicBacklog' | 'allReports' | 'questions' | 'settings' | 'project';
const REPORT_SECTIONS: readonly SettingsSection[] = ['backlog', 'board', 'taskList', 'epics', 'epicBacklog', 'allReports'];
/** Мн4: the tabs of «Проект» that live in this screen (the people of the project are the tracker's own screen). */
export type ProjectPart = 'profile' | 'voice' | 'prompts';

/** `tracker` — the tracker site (tracker.jaiora.me): adds «Пользователи» for a super of a project that reads all. */
export default function BugReportSettings({
  onApiError,
  tracker = false,
  section = 'all',
  part = 'profile',
  stage,
}: {
  onApiError?: (err: unknown) => void;
  tracker?: boolean;
  section?: SettingsSection;
  /** Мн4: which tab of «Проект» (`section: 'project'`). */
  part?: ProjectPart;
  /** Мн2: `taskList` opens already filtered by this stage («#backlog»). */
  stage?: 'backlog';
}) {
  const [settings, setSettings] = useState<BugSettings | null>(null);
  const [error, setError] = useState('');
  /** 503: the proxy has no service, or the service has no database — the reason in words. */
  const [unavailable, setUnavailable] = useState('');
  /** 403: not super — the page shows only the person's own questions. */
  const [ownOnly, setOwnOnly] = useState(false);
  const [questions, setQuestions] = useState<QuestionCount | null>(null);
  const { confirm, dialog } = useConfirm();

  const fail = useCallback(
    (err: unknown, fallback: string) => {
      onApiError?.(err);
      setError(errorOf(err, fallback));
    },
    [onApiError],
  );

  useEffect(() => {
    bugApi
      .settings()
      .then((res) => setSettings(res.settings))
      .catch((err) => {
        if (err instanceof ApiError && err.status === 503) {
          onApiError?.(err);
          setUnavailable(err.message);
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          setOwnOnly(true);
          return;
        }
        fail(err, 'Не удалось загрузить настройки баг-репортов');
      });
  }, [fail, onApiError]);

  const screen = (children: ReactNode) => (
    <ConfirmContext.Provider value={confirm}>
      <div className="jui-stack bug-settings">
        {children}
        {dialog}
      </div>
    </ConfirmContext.Provider>
  );
  const errorBanner = error && <Banner tone="error">{error}</Banner>;

  if (unavailable) return screen(<Banner tone="warning">{unavailable}</Banner>);
  if (ownOnly) {
    // Т5: not a super — own questions only, and only in «Вопросы» (a host keeps them on its one screen).
    if (section !== 'all' && section !== 'questions') return screen(<EmptyState compact>Раздел — для суперадмина.</EmptyState>);
    return screen(
      <>
        {errorBanner}
        <QuestionList onError={fail} untitled={tracker} />
      </>,
    );
  }
  if (!settings) return screen(errorBanner || <Loading />);

  const prompts = settingsConfig().prompts;
  /** A status changed or a report went away on this screen: the counters follow (tracker_plain Тр2 countsTrue). */
  const moved = (project: string, from: BugStatus, to: BugStatus | null) =>
    setSettings((prev) => (prev ? { ...prev, counts: moveCount(prev.counts, project, from, to) } : prev));

  const ready = settings.enabled && !settings.problem;
  const show = (part: Exclude<SettingsSection, 'all'>) => section === 'all' || section === part;

  if (section === 'questions') {
    return screen(
      <>
        {errorBanner}
        {ready && <QuestionList onError={fail} onCount={setQuestions} untitled={tracker} />}
      </>,
    );
  }
  if (section === 'project') {
    return screen(
      <>
        {errorBanner}
        {ready && <ProjectPane settings={settings} part={part} onSaved={setSettings} onError={fail} prompts={prompts} />}
      </>,
    );
  }
  if (section === 'settings') {
    return screen(
      <>
        {errorBanner}
        {ready && <TrackerSettings settings={settings} onSaved={setSettings} onError={fail} prompts={prompts} tracker={tracker} />}
      </>,
    );
  }

  return screen(
    <>
      {errorBanner}
      {!settings.enabled ? (
        <Banner tone="warning" title="Хранилище не подключено">
          Репорты не принимаются.
          {settings.problem ? ` ${settings.problem}` : ''}
        </Banner>
      ) : settings.problem ? (
        <Banner tone="error" title={`База «${settings.database}» не отвечает`}>
          {settings.problem}
        </Banner>
      ) : (
        (section === 'all' || section === 'reports' || section === 'board') && <Overview settings={settings} questions={questions} />
      )}

      {ready && (
        <>
          {REPORT_SECTIONS.includes(section) ? (
            <ReportView view={VIEW_OF_SECTION[section as keyof typeof VIEW_OF_SECTION]} stage={stage} onError={fail} onMoved={moved} />
          ) : (
            (tracker ? <ReportView view="board" onError={fail} onMoved={moved} /> : <TrackerLink url={settings.trackerUrl} />)
          )}
          {show('questions') && <QuestionList onError={fail} onCount={setQuestions} />}
          {show('settings') && <TrackerSettings settings={settings} onSaved={setSettings} onError={fail} prompts={prompts} tracker={tracker} />}
        </>
      )}
      {/* Т5: «Вопросы» is a tab of its own — the overview still counts them. */}
      {ready && (section === 'reports' || section === 'board') && <QuestionCounter onCount={setQuestions} />}
    </>,
  );
}

/** Profile, voice, prompts, MCP access (and «Проекты» for a host) — «Настройки» of the tracker site (Т5). */
function TrackerSettings({
  settings,
  onSaved,
  onError,
  prompts,
  tracker,
}: {
  settings: BugSettings;
  onSaved: Saved;
  onError: Failed;
  prompts: boolean;
  tracker: boolean;
}) {
  return (
    <section className="jui-panel bug-sfolds" aria-label="Настройки трекера">
      <ProjectSettings settings={settings} onSaved={onSaved} onError={onError} prompts={prompts} />
      <Fold title="Доступ по MCP" summary={`токены агентов · только «${settings.project}»`}>
        <McpAccess settings={settings} onError={onError} />
      </Fold>
      {!tracker && settings.canRegister && (
        <Fold title="Проекты" summary="регистрация хостов · ключи jbproj_">
          <ProjectRegistry onError={onError} />
        </Fold>
      )}
    </section>
  );
}

/** Мн4: one tab of «Проект» — a block of its own, not a fold among others. */
function ProjectPane({
  settings,
  part,
  onSaved,
  onError,
  prompts,
}: {
  settings: BugSettings;
  part: ProjectPart;
  onSaved: Saved;
  onError: Failed;
  prompts: boolean;
}) {
  const target = settings.project;
  if (part === 'voice') {
    return (
      <section className="jui-stack bug-block" aria-label="Голос">
        <BlockHead title="Голос" hint="Какая модель превращает записанный голос в текст репорта." />
        <TranscribeForm key={`voice:${target}`} settings={settings} onSaved={onSaved} onError={onError} />
      </section>
    );
  }
  const promptList = prompts && settings.prompts?.length ? (
    <section className="jui-panel bug-sfolds" aria-label="Промпты">
      <div className="bug-sfold-list">
        {settings.prompts.map((prompt) => (
          <Fold key={`${target}:${prompt.key}`} title={prompt.title} summary={`${prompt.custom ? 'своя правка' : 'умолчание'}${prompt.hint ? ` · ${prompt.hint}` : ''}`}>
            <PromptForm prompt={prompt} onSaved={onSaved} onError={onError} />
          </Fold>
        ))}
      </div>
    </section>
  ) : (
    <EmptyState compact>Промптов нет.</EmptyState>
  );
  if (part === 'prompts') return promptList;
  // Ра8: «Профиль» — один экран, два блока: о чём проект и что мы просим у модели; у каждого — куда это уходит.
  return (
    <div className="jui-stack">
      <section className="jui-stack bug-block" aria-label="О проекте">
        <BlockHead title="О проекте" hint="Что это за приложение: название, описание, слова для голоса." />
        {settings.profile ? <ProfileForm key={target} settings={settings} onSaved={onSaved} onError={onError} /> : <EmptyState compact>Профиля нет.</EmptyState>}
      </section>
      <section className="jui-stack bug-block" aria-label="Инструкции модели">
        <BlockHead title="Инструкции модели" hint="Что и как мы просим у модели. Профиль выше подставляется в каждую инструкцию." />
        {promptList}
      </section>
    </div>
  );
}

/** Ра8, Вп7: the head of a block of a screen — what it is, in a line. */
function BlockHead({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="bug-block-head">
      <h2 className="bug-block-title">{title}</h2>
      <span className="jui-muted">{hint}</span>
    </div>
  );
}

/** The overview's question tile when the list itself lives on another tab. */
function QuestionCounter({ onCount }: { onCount: (count: QuestionCount) => void }) {
  useEffect(() => {
    bugApi
      .questions(null)
      .then((res) => onCount({ count: res.questions.length, more: !!res.nextBefore }))
      .catch(() => undefined);
  }, [onCount]);
  return null;
}

/**
 * Tracker site tabs «Проекты» (an operator) and «Пользователи» (a super of the project, an operator): host registration and the
 * tracker accounts, each a screen of its own instead of a fold under the settings.
 */
export function TrackerAdmin({
  section,
  operator = false,
  selected,
  onApiError,
}: {
  /** Тг1: tabs of the shared area «Трекер» — `projects` (the operator), `tokens` (access MCP), `users` (people). */
  section: 'projects' | 'tokens' | 'users';
  operator?: boolean;
  /** Мд4: the registry shares the project choice of the shell and opens the tabs of that project. */
  selected?: RegistrySelection;
  onApiError?: (err: unknown) => void;
}) {
  const [error, setError] = useState('');
  const { confirm, dialog } = useConfirm();
  const fail = useCallback(
    (err: unknown, fallback: string) => {
      onApiError?.(err);
      setError(errorOf(err, fallback));
    },
    [onApiError],
  );
  return (
    <ConfirmContext.Provider value={confirm}>
      <div className="jui-stack bug-settings">
        {error && <Banner tone="error">{error}</Banner>}
        {/* The page header names the section (AppShell + PageHeader): no second title here. */}
        {section === 'projects' && <ProjectRegistry onError={fail} selected={selected} />}
        {section === 'tokens' && <RegistryTokens onError={fail} scope={operator ? 'all' : 'project'} />}
        {section === 'users' && <TrackerUsers onError={fail} operator={operator} scope={operator ? 'all' : 'project'} />}
        {dialog}
      </div>
    </ConfirmContext.Provider>
  );
}

/** Тг1, Тг4: «Трекер → Доступ MCP» — the operator sees every token of every project, a super of one project those that reach it. */
function RegistryTokens({ onError, scope }: { onError: Failed; scope: 'project' | 'all' }) {
  const [settings, setSettings] = useState<BugSettings | null>(null);
  useEffect(() => {
    bugApi
      .settings()
      .then((res) => setSettings(res.settings))
      .catch((err) => onError(err, 'Не удалось загрузить настройки'));
  }, [onError]);
  return settings ? <McpAccess settings={settings} onError={onError} scope={scope} /> : <Loading />;
}

/** Пл3: where the detail of a project leads — a tab of the project of the row, whatever the shell works in. */
export interface RegistrySelection {
  /** Ра6: a tab of «Проект» or «Трекер» of a named project — the project is picked first, so the tab opens for it and the address names it. */
  openProject: (name: string, tab: string) => void;
}

/** Пл2, Пл3: the detail of the project of the clicked row — what it is, who leads it, what reaches it, and the way into its tabs. */
function ProjectCard({ project, selection }: { project: RegistryProject; selection: RegistrySelection }) {
  const open = (tab: string) => selection.openProject(project.name, tab);
  return (
    <div className="jui-stack bug-project-card">
      <div className="jui-row bug-cell">
        {project.revokedAt ? <Badge tone="error">ключ отозван</Badge> : <Badge>работает</Badge>}
        {project.readsAll && <Badge tone="accent">видит все</Badge>}
        <span className="jui-muted mono">ключ хоста {project.keyPrefix}…</span>
      </div>
      {project.mcp && <CopyValue value={project.mcp.url} tip="Адрес MCP проекта: токен с правом на проект видит только его" />}
      <div className="jui-row bug-cell">
        <Metric className="bug-tile" value={project.users ?? 0} short="людей" label="Людей в проекте" />
        <Metric className="bug-tile" value={project.operators ?? 0} short="операторов во всех проектах" label="Операторы других проектов: доступ ко всем проектам" />
        <Metric className="bug-tile" value={project.tokens ?? 0} short="токенов" label="Токенов MCP" />
        <Metric className="bug-tile" value={project.reports} short="репортов" label="Репортов" />
      </div>
      {project.owners && project.owners.length > 0 && <span className="jui-muted">Владелец: {project.owners.join(', ')}</span>}
      <div className="jui-row bug-actions">
        <Button size="sm" onClick={() => open('profile')}>
          Профиль
        </Button>
        <Button size="sm" onClick={() => open('users')}>
          Пользователи
        </Button>
        <Button size="sm" onClick={() => open('mcp')}>
          Токены MCP
        </Button>
        <Button size="sm" onClick={() => open('host')}>
          Ключ хоста
        </Button>
        <Button size="sm" onClick={() => open('features')}>
          Возможности
        </Button>
      </div>
    </div>
  );
}

const GRANT_OPTIONS: SelectOption[] = [
  { value: 'none', label: 'нет доступа' },
  { value: 'read', label: 'Только чтение' },
  { value: 'write', label: 'Чтение и запись' },
];

/** Т14: why «Отозвать ключ» is off for this row (null — it is on). */
export const revokeOff = (project: { own?: boolean; revokedAt?: string | null }): string | null =>
  project.own ? 'свой ключ — только командой project на сервере' : project.revokedAt ? `уже отозван ${formatDateTime(project.revokedAt)}` : null;

export const copyText = (text: string, done: (ok: boolean) => void) => {
  void navigator.clipboard?.writeText(text).then(
    () => done(true),
    () => done(false),
  );
};

/** A secret shown once (a token, a key, a password): `.token-secret` keeps it out of shots and the page HTML. */
export function SecretBlock({ title, note, actions, children }: { title: ReactNode; note: ReactNode; actions: ReactNode; children: ReactNode }) {
  return (
    <div className="token-secret jui-stack">
      <Banner tone="warning" title={title} actions={actions}>
        {note}
      </Banner>
      {children}
    </div>
  );
}

/**
 * «Пользователи» of the tracker site: accounts, their project (visibility) and role. A new password is shown once; a
 * reset ends the person's sessions. The caller is never deleted or demoted here (tracker_access П304).
 */
function TrackerUsers({
  onError,
  operator,
  scope = 'all',
}: {
  onError: Failed;
  operator: boolean;
  /** Тг4: `all` — every person of every project (the operator), `project` — the people of the working project (a super of it). */
  scope?: 'project' | 'all';
}) {
  const ask = useAsk();
  const [data, setData] = useState<TrackerUserList | null>(null);
  const [username, setUsername] = useState('');
  const [isSuper, setIsSuper] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [issued, setIssued] = useState<{
    username: string;
    password: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    bugApi
      .trackerUsers(scope)
      .then(setData)
      .catch((err) => onError(err, 'Не удалось загрузить пользователей'));
  }, [onError, scope]);

  const run = async (action: () => Promise<TrackerUserList & Partial<IssuedPassword>>, fallback: string) => {
    setBusy(true);
    try {
      const res = await action();
      setData({ users: res.users, projects: res.projects, project: res.project, canManage: res.canManage });
      if (res.password && res.username) {
        setIssued({ username: res.username, password: res.password });
        setCopied(false);
      }
      return true;
    } catch (err) {
      onError(err, fallback);
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Ра5: a new person goes to the working project — there is no choice of project on this tab.
  const target = data?.project ?? data?.projects[0] ?? '';
  const canAdd = data?.canManage ?? !!data?.users.some((user) => user.manageable);
  const [projectsOf, setProjectsOf] = useState<TrackerUserList['users'][number] | null>(null);
  const [pickedProjects, setPickedProjects] = useState<string[]>([]);
  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (
      await run(
        () =>
          bugApi.addTrackerUser(
            {
              username: username.trim(),
              project: target,
              isSuper,
            },
            scope,
          ),
        'Не удалось завести пользователя',
      )
    ) {
      setUsername('');
      setIsSuper(false);
      setFormOpen(false);
    }
  };

  type User = TrackerUserList['users'][number];
  const lastOperator = (data?.users.filter((user) => user.operator).length ?? 0) <= 1;
  const projectColumn: Column<User> = {
    key: 'project',
    width: '28%',
    header: 'Проекты',
    value: (user) => (user.projects ?? [user.project]).join(' '),
    sortable: true,
    // The first is the own project, the rest were added by an operator; one text so a long name ellipsises like any cell.
    render: (user) => <span className="mono" title="Основной проект первым, дальше добавленные оператором">{(user.projects ?? [user.project]).join(', ')}</span>,
  };
  const columns: Column<User>[] = [
    {
      key: 'username',
      width: scope === 'all' ? '36%' : '60%',
      header: 'Логин',
      value: (user) => user.username,
      sortable: true,
      render: (user) => (
        <span className="jui-row bug-cell">
          <b>{user.username}</b>
          {user.self && <Badge>вы</Badge>}
          {user.isSuper && <Badge tone="accent">суперадмин</Badge>}
          {user.operator && <Badge tone="accent">оператор</Badge>}
        </span>
      ),
    },
    projectColumn,
    {
      key: 'createdAt',
      width: scope === 'all' ? '24%' : '28%',
      header: 'Заведён',
      value: (user) => new Date(user.createdAt),
      sortable: true,
      render: (user) => <span className="jui-muted nowrap">{formatDateTime(user.createdAt)}</span>,
    },
    {
      key: 'actions',
      width: 72,
      header: '',
      headerLabel: 'Действия',
      align: 'right',
      sortable: false,
      filterable: false,
      render: (user) => (
        <RowMenu
          label={`Действия: ${user.username}`}
          actions={[
            ...(operator
              ? [{
                  label: 'Проекты доступа',
                  disabled: busy,
                  run: () => {
                    setPickedProjects((user.projects ?? [user.project]).filter((name) => name !== user.project));
                    setProjectsOf(user);
                  },
                }]
              : []),
            ...(!user.manageable
              ? []
              : [{
                  label: 'Новый пароль',
                  disabled: busy,
                  run: async () => {
                    const yes = await ask({
                      title: `Новый пароль для «${user.username}»?`,
                      text: 'Прежний перестанет работать, открытые входы закроются.',
                      confirmLabel: 'Новый пароль',
                      tone: 'primary' as const,
                    });
                    if (yes) void run(() => bugApi.resetTrackerPassword(user.id, scope), 'Не удалось сменить пароль');
                  },
                }]),
            // Ра5: the operator role is a cross-project one — it is given and taken in the registry.
            ...(operator && scope === 'all'
              ? [
                  {
                    label: user.operator ? 'Снять роль оператора' : 'Сделать оператором',
                    disabled: busy || (user.operator && lastOperator),
                    ...(user.operator && lastOperator ? { hint: 'последнего оператора снять нельзя' } : {}),
                    run: () => void run(() => bugApi.setTrackerOperator(user.id, !user.operator, scope), 'Не удалось сменить роль оператора'),
                  },
                ]
              : []),
            ...(user.self || !user.manageable
              ? []
              : [
                  {
                    label: user.isSuper ? 'Снять суперадмина' : 'Сделать суперадмином',
                    disabled: busy,
                    run: () => void run(() => bugApi.setTrackerRole(user.id, !user.isSuper, scope), 'Не удалось сменить роль'),
                  },
                  {
                    label: 'Удалить',
                    disabled: busy,
                    run: async () => {
                      const yes = await ask({
                        title: `Удалить «${user.username}»?`,
                        text: 'Вход в трекер закроется сразу.',
                      });
                      if (yes) void run(() => bugApi.removeTrackerUser(user.id, scope), 'Не удалось удалить');
                    },
                  },
                ]),
          ]}
        />
      ),
    },
  ];

  return (
    <div className="jui-stack">
      {/* Ра2, Ра7: the action stands in the header of the page; a drawer holds the form. */}
      {canAdd && (
        <HeaderActions>
          <Button variant="primary" size="sm" icon={<PlusIcon />} onClick={() => setFormOpen(true)}>
            Завести пользователя
          </Button>
        </HeaderActions>
      )}
      {data && !canAdd && <Hint>Людей проекта «{target}» заводит и правит его суперадмин; вы их только видите.</Hint>}
      {data && canAdd && (
        <Hint>
          {scope === 'all'
            ? 'Люди всех проектов. Правит людей своего проекта суперадмин; проекты доступа и роль оператора — оператор.'
            : `Люди проекта «${target}» и их роли. Суперадмин проекта правит людей; проекты доступа и роль оператора — оператор.`}
        </Hint>
      )}
      {issued && (
        <SecretBlock
          title={`Пароль «${issued.username}»`}
          note="передайте сейчас, второй раз он не покажется"
          actions={
            <>
              <Button size="sm" variant="primary" onClick={() => copyText(issued.password, setCopied)}>
                {copied ? 'Скопировано' : 'Копировать'}
              </Button>
              <Button size="sm" variant="quiet" onClick={() => setIssued(null)}>
                Скрыть
              </Button>
            </>
          }
        >
          <div className="token-value mono">{issued.password}</div>
        </SecretBlock>
      )}

      {data === null ? (
        <Loading />
      ) : data.users.length === 0 ? (
        <EmptyState compact>В проекте пока никого нет{canAdd ? ': «Завести пользователя» — первый логин и пароль' : ''}.</EmptyState>
      ) : (
        <DataTable className="bug-fixed-table" label="Пользователи" columns={columns} rows={data.users} rowKey={(user) => user.id} pageSize={0} filter={{ placeholder: scope === 'all' ? 'Логин, проект' : 'Логин' }} />
      )}

      <Drawer
        open={!!projectsOf}
        onClose={() => setProjectsOf(null)}
        size="sm"
        title={`Проекты · ${projectsOf?.username ?? ''}`}
        footer={
          <Button
            variant="primary"
            disabled={busy}
            onClick={async () => {
              if (projectsOf && (await run(() => bugApi.setTrackerUserProjects(projectsOf.id, pickedProjects, scope), 'Не удалось сохранить проекты'))) setProjectsOf(null);
            }}
          >
            Сохранить
          </Button>
        }
      >
        <div className="jui-stack bug-form">
          <Hint>Основной проект остаётся всегда; отмеченные — те, в которых человек ещё может работать.</Hint>
          {(data?.allProjects ?? []).map((name) => (
            <label key={name} className="jui-row bug-check">
              <input
                type="checkbox"
                className="jui-checkbox"
                checked={name === projectsOf?.project || pickedProjects.includes(name)}
                disabled={name === projectsOf?.project}
                onChange={(e) => setPickedProjects((list) => (e.target.checked ? [...list, name] : list.filter((item) => item !== name)))}
              />{' '}
              <span className="mono">{name}</span>
            </label>
          ))}
        </div>
      </Drawer>
      <Drawer
        open={formOpen}
        onClose={() => setFormOpen(false)}
        size="sm"
        title="Новый пользователь"
        footer={
          <Button type="submit" form="bug-user-form" variant="primary" disabled={busy || !username.trim() || !target}>
            Завести
          </Button>
        }
      >
        <form id="bug-user-form" onSubmit={create} className="jui-stack bug-form">
          <TextField
            label="Логин"
            hint={`Войдёт в проект «${target}»; пароль покажем один раз`}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="логин"
            maxLength={64}
            required
          />
          <label className="jui-row bug-check">
            <input type="checkbox" className="jui-checkbox" checked={isSuper} onChange={(e) => setIsSuper(e.target.checked)} /> суперадмин
          </label>
        </form>
      </Drawer>
    </div>
  );
}

const OPEN_STATUSES: readonly BugStatus[] = ['backlog', 'requirements', 'validation', 'implementation', 'deploy'];

/** Counts after a status change on the screen: one less under `from`, one more under `to` (null — deleted). */
export function moveCount(counts: BugSettings['counts'], project: string, from: BugStatus, to: BugStatus | null): BugSettings['counts'] {
  if (from === to) return counts;
  const next = counts.map((row) => (row.project === project && row.status === from ? { ...row, count: Math.max(0, row.count - 1) } : row));
  if (!to) return next;
  return next.some((row) => row.project === project && row.status === to)
    ? next.map((row) => (row.project === project && row.status === to ? { ...row, count: row.count + 1 } : row))
    : [...next, { project, status: to, count: 1 }];
}

/** Questions on the screen: the loaded ones, `more` — there are older ones. */
interface QuestionCount {
  count: number;
  more: boolean;
}

/**
 * The tracker at a glance (report 63 of CRM: «мало интерфейсов, которые объясняют»): counters, statuses as a bar, how
 * reports get here in three steps, and the service state as badges — no paragraph.
 */
function Overview({ settings, questions }: { settings: BugSettings; questions: QuestionCount | null }) {
  const byStatus = new Map<BugStatus, number>();
  for (const row of settings.counts) byStatus.set(row.status, (byStatus.get(row.status) ?? 0) + row.count);
  const total = [...byStatus.values()].reduce((sum, count) => sum + count, 0);
  const open = OPEN_STATUSES.reduce((sum, status) => sum + (byStatus.get(status) ?? 0), 0);
  const model = settings.model;
  const segments: StatSegment[] = ALL_STATUSES.filter((status) => byStatus.get(status)).map((status) => ({
    label: BUG_STATUS_LABELS[status],
    value: byStatus.get(status) ?? 0,
    tone: STATUS_TONES[status],
  }));
  return (
    <section className="jui-stack bug-overview" aria-label="Трекер">
      <Toolbar className="bug-tiles">
        <Metric className="bug-tile" value={open} short="открытых" label="Открытых репортов" tone="error" dim={open === 0} />
        <Metric className="bug-tile" value={total - open} short="закрытых" label="Закрытых репортов" tone="success" dim={total - open === 0} />
        <Metric
          className="bug-tile"
          value={questions ? `${questions.count}${questions.more ? '+' : ''}` : '…'}
          short="вопросов"
          label="Вопросов"
          tone="info"
          dim={questions?.count === 0}
        />
        {/* Рб2: the backlog is one press away — a tile like the others, a link to the backlog list */}
        <a href="#backlog" className="bug-tile-link" aria-label="Бэклог: открыть список">
          <Metric className="bug-tile" value={byStatus.get('backlog') ?? 0} short="в бэклоге" label="Репортов в бэклоге — открыть список" tone="info" dim={(byStatus.get('backlog') ?? 0) === 0} />
        </a>
      </Toolbar>
      {total > 0 && <StatBar label="По статусам" segments={segments} />}
      {model && !model.available && <Banner tone="warning" title={`Модель недоступна${model.problem ? `: ${model.problem}` : ''}`} />}
    </section>
  );
}

/** A long form folded into one row; one click opens it. */
function Fold({ title, summary, children }: { icon?: ReactNode; title: string; summary?: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <Row
      className={`bug-sfold${open ? ' bug-sfold-open' : ''}`}
      density="comfortable"
      title={<b>{title}</b>}
      meta={
        <>
          {summary && <span className="bug-sfold-sum">{summary}</span>}
          <ChevronIcon open={open} />
        </>
      }
      expanded={open}
      onClick={() => setOpen(!open)}
    >
      {open && <div className="bug-sfold-body">{children}</div>}
    </Row>
  );
}

type Saved = (settings: BugSettings) => void;
type Failed = (err: unknown, fallback: string) => void;

/** `?project=` only for another project: this host's own settings keep the plain address. */
/** «своя правка: who, when» or «умолчание» — as a badge. */
function EditMark({ custom, by, at }: { custom: boolean; by?: string | null; at?: string | null }) {
  return custom ? <Badge tone="accent">своя правка{by ? `: ${by}, ${formatDateTime(at ?? null)}` : ''}</Badge> : <Badge>умолчание</Badge>;
}

/**
 * What belongs to the working project: its profile (what the app is — for the voice and MCP), the transcribe model and —
 * only for a host that asked (`settings.prompts`) — the prompts; each folded into a row. Пр1: one project at a time,
 * picked in the page header of the tracker.
 */
function ProjectSettings({ settings, onSaved, onError, prompts }: { settings: BugSettings; onSaved: Saved; onError: Failed; prompts: boolean }) {
  const target = settings.project;
  const saved: Saved = onSaved;
  const profile = settings.profile;
  const customPrompts = (settings.prompts ?? []).filter((prompt) => prompt.custom).length;
  return (
    <section className="jui-stack bug-project-settings" aria-label="Настройки проекта">
      <div className="bug-sfold-list">
        {profile && (
          <Fold
            title="Профиль проекта"
            summary={`${profile.title.value} · ${[profile.title, profile.context, profile.glossary, profile.agentProcess].some((field) => field?.custom) ? 'своя правка' : 'умолчание'}`}
          >
            <ProfileForm key={target} settings={settings} onSaved={saved} onError={onError} />
          </Fold>
        )}
        <Fold title="Голос" summary={`${settings.transcribe.model}${settings.transcribe.custom ? ' · своя' : ''}`}>
          <TranscribeForm key={`voice:${target}`} settings={settings} onSaved={saved} onError={onError} />
        </Fold>
        {prompts && settings.prompts && settings.prompts.length > 0 && (
          <Fold title="Промпты" summary={`${settings.prompts.length}${customPrompts ? ` · своих правок ${customPrompts}` : ''}`}>
            <div className="jui-stack">
              {settings.prompts.map((prompt) => (
                <PromptForm key={`${target}:${prompt.key}`} prompt={prompt} onSaved={saved} onError={onError} />
              ))}
            </div>
          </Fold>
        )}
      </div>
    </section>
  );
}

const PROFILE_FIELDS: {
  key: BugProfileKey;
  label: string;
  hint: string;
  rows: number;
}[] = [
  {
    key: 'title',
    label: 'Название',
    hint: 'Как приложение называется на экране: в промптах, подсказке голосу и у репортов в MCP.',
    rows: 1,
  },
  {
    key: 'context',
    label: 'Описание',
    hint: 'Что это за приложение, кто в нём работает и в каких ролях, разделы и их слова, какие данные людей видны на экране. Уходит в системную часть промптов и агенту в MCP. Не короче 20 знаков. Пример: «Админка кураторов: кандидаты, вакансии, переписка. Роли: админ и суперадмин».',
    rows: 10,
  },
  {
    key: 'glossary',
    label: 'Словарь для голоса',
    hint: 'Слова через запятую, которые люди говорят вслух: названия разделов, ролей, терминов. Подсказка расшифровке голоса, чтобы она не коверкала их. Пример: «воронка, ситилид, карточка участника».',
    rows: 3,
  },
  {
    key: 'agentProcess',
    label: 'Процесс агента',
    hint: 'Куда уходит: агенту в MCP. Как агент ведёт репорт по этапам: требование, модель, код, выкладка, «Сделано». Видят инструкции MCP, ответы next_steps и take_next. Пусто — флоу Jaiora по умолчанию.',
    rows: 10,
  },
];

function ProfileForm({ settings, onSaved, onError }: { settings: BugSettings; onSaved: Saved; onError: Failed }) {
  const profile = settings.profile!;
  return (
    <div className="jui-stack bug-narrow">
      <Hint>
        {profile.seed
          ? `Умолчание — из кода (профиль «${profile.seed}»${profile.base !== profile.name ? `, по базовой метке ${profile.base}` : ''}).`
          : 'Профиля этого проекта в коде нет — умолчание собрано из метки; опишите приложение здесь.'}
      </Hint>
      {PROFILE_FIELDS.flatMap((item) => {
        const field = profile[item.key];
        // an older service does not know «Процесс агента»: no field, no form
        return field
          ? [
              <ProfileFieldForm
                key={item.key}
                name={item.key}
                label={item.label}
                hint={item.hint}
                rows={item.rows}
                title={profile.title.value}
                field={field}
                onSaved={onSaved}
                onError={onError}
              />,
            ]
          : [];
      })}
    </div>
  );
}

/** Гл3: how many words of the glossary reach the transcription prompt — the same count the service takes. */
function GlossaryFit({ text, title }: { text: string; title: string }) {
  const { total, used } = hintFit(text ?? '', hintHead(title ?? '').length);
  const long = longWord(glossaryWords(text ?? ''));
  if (long) return <Hint>Слово «{long.slice(0, 30)}…» длиннее {MAX_WORD} знаков: разделяйте слова запятыми.</Hint>;
  return (
    <Hint>
      {total === 0 ? 'Слов нет.' : used < total ? `В подсказку войдёт ${used} из ${total} слов — лишнее в конце не учтётся.` : `В подсказку войдут все ${total} слов.`}
    </Hint>
  );
}

function ContextCount({ text, limit }: { text: string; limit: number }) {
  return <Hint>{`${(text ?? '').trim().length} из ${limit} знаков`}</Hint>;
}

/** Save · back to the default · cancel, and what the field holds now. */
function FormActions({
  busy,
  canSave,
  canReset,
  dirty,
  onReset,
  onCancel,
  notice,
  mark,
}: {
  busy: boolean;
  canSave: boolean;
  canReset: boolean;
  dirty: boolean;
  onReset: () => void;
  onCancel: () => void;
  notice: string;
  mark?: ReactNode;
}) {
  return (
    <div className="jui-row">
      <Button type="submit" size="sm" variant="primary" disabled={busy || !canSave}>
        Сохранить
      </Button>
      <Button size="sm" variant="quiet" disabled={busy || !canReset} onClick={onReset}>
        Вернуть умолчание
      </Button>
      {dirty && (
        <Button size="sm" variant="quiet" disabled={busy} onClick={onCancel}>
          Отменить
        </Button>
      )}
      {mark}
      {notice && <Badge tone="success">{notice}</Badge>}
    </div>
  );
}

function ProfileFieldForm({
  name,
  label,
  hint,
  rows,
  title,
  field,
  onSaved,
  onError,
}: {
  name: BugProfileKey;
  label: string;
  hint: string;
  rows: number;
  title: string;
  field: BugProfileField;
  onSaved: Saved;
  onError: Failed;
}) {
  const ask = useAsk();
  const [text, setText] = useState(field.value);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    setText(field.value);
  }, [field]);

  const save = async (value: string | null, done: string) => {
    setBusy(true);
    setNotice('');
    try {
      const res = await bugApi.saveSettings({ profile: { [name]: value } });
      onSaved(res.settings);
      setNotice(done);
    } catch (err) {
      onError(err, `Не удалось сохранить: ${label.toLowerCase()}`);
    } finally {
      setBusy(false);
    }
  };

  const changed = text !== field.value && !!text.trim();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (changed) void save(text, 'Сохранено.');
  };
  const reset = async () => {
    if (
      !(await ask({
        title: `Вернуть умолчание «${label}»?`,
        text: 'Своя правка пропадёт.',
        confirmLabel: 'Вернуть',
        tone: 'primary',
      }))
    )
      return;
    void save(null, 'Вернули умолчание.');
  };

  const control = {
    label,
    hint,
    value: text,
    maxLength: field.maxLength,
    disabled: busy,
    spellCheck: false,
  };
  return (
    <form className={`jui-stack bug-card bug-profile-field bug-profile-${name}`} onSubmit={submit} aria-label={label}>
      {rows === 1 ? (
        <TextField {...control} onChange={(e) => setText(e.target.value)} />
      ) : (
        <TextArea {...control} rows={rows} onChange={(e) => setText(e.target.value)} />
      )}
      {name === 'glossary' && <GlossaryFit text={text} title={title} />}
      {name === 'context' && <ContextCount text={text} limit={field.maxLength} />}
      <FormActions
        busy={busy}
        canSave={changed}
        canReset={field.custom}
        dirty={text !== field.value}
        onReset={() => void reset()}
        onCancel={() => setText(field.value)}
        notice={notice}
        mark={<EditMark custom={field.custom} by={field.updatedBy} at={field.updatedAt} />}
      />
    </form>
  );
}

/** A prompt's user part: how to judge; the system part is the contract with the service code and is shown read-only. */
function PromptForm({ prompt, onSaved, onError }: { prompt: BugPrompt; onSaved: Saved; onError: Failed }) {
  const ask = useAsk();
  const [text, setText] = useState(prompt.user);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    setText(prompt.user);
  }, [prompt]);

  const save = async (value: string | null, done: string) => {
    setBusy(true);
    setNotice('');
    try {
      const res = await bugApi.saveSettings({ prompts: { [prompt.key]: value } });
      onSaved(res.settings);
      setNotice(done);
    } catch (err) {
      onError(err, 'Не удалось сохранить промпт');
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (text === prompt.user || !text.trim()) return;
    void save(text, 'Сохранено.');
  };

  // The service marks where the user part goes inside the system message; shown in that order, as it reaches the model.
  const [head, tail = ''] = prompt.system.split(USER_PART_MARK);

  const reset = async () => {
    if (
      !(await ask({
        title: `Вернуть умолчание «${prompt.title}»?`,
        text: 'Своя правка пропадёт.',
        confirmLabel: 'Вернуть',
        tone: 'primary',
      }))
    )
      return;
    void save(null, 'Вернули умолчание.');
  };

  return (
    <form className="jui-stack bug-card bug-prompt" onSubmit={submit} aria-label={prompt.title}>
      <div className="jui-row">
        <b>{prompt.title}</b>
      </div>
      {prompt.hint && <Hint>{prompt.hint}</Hint>}
      {head.trim() && (
        <div className="jui-field">
          <span className="jui-label">Системная часть · только чтение</span>
          <pre className="prompt-fixed">{head.trim()}</pre>
        </div>
      )}
      <TextArea
        className="prompt-user"
        label="Пользовательская часть"
        rows={8}
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={MAX_PROMPT}
        disabled={busy}
        spellCheck={false}
      />
      {tail.trim() && (
        <div className="jui-field">
          <span className="jui-label">Конец системной части · только чтение</span>
          <pre className="prompt-fixed">{tail.trim()}</pre>
        </div>
      )}
      <FormActions
        busy={busy}
        canSave={text !== prompt.user && !!text.trim()}
        canReset={prompt.custom}
        dirty={text !== prompt.user}
        onReset={() => void reset()}
        onCancel={() => setText(prompt.user)}
        notice={notice}
        mark={<EditMark custom={prompt.custom} by={prompt.updatedBy} at={prompt.updatedAt} />}
      />
    </form>
  );
}

function TranscribeForm({
  settings,
  onSaved,
  onError,
}: {
  settings: BugSettings;
  onSaved: (settings: BugSettings) => void;
  onError: (err: unknown, fallback: string) => void;
}) {
  const ask = useAsk();
  const t = settings.transcribe;
  const saved = t.custom ? t.model : '';
  const [model, setModel] = useState(saved);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  // Гл1: only a pick from the list; a saved name that has left the list is shown as such and replaced by a pick.
  const options: SelectOption[] = [
    { value: '', label: `По умолчанию — ${t.model}`, hint: t.custom ? 'без своей правки' : 'сейчас' },
    ...(t.models ?? []).map((item) => ({ value: item.id, label: item.label, hint: item.hint })),
  ];
  if (saved && !options.some((item) => item.value === saved)) {
    options.push({ value: saved, label: saved, hint: 'нет в списке — выберите другую' });
  }
  const write = async (value: string, done: string) => {
    setBusy(true);
    setNotice('');
    try {
      const res = await bugApi.saveSettings({ transcribeModel: value || null });
      onSaved(res.settings);
      setNotice(done);
    } catch (err) {
      onError(err, 'Не удалось сохранить модель расшифровки');
    } finally {
      setBusy(false);
    }
  };
  const save = (e: FormEvent) => {
    e.preventDefault();
    if (model !== saved) void write(model, 'Сохранено.');
  };
  const reset = async () => {
    if (!(await ask({ title: 'Вернуть умолчание «Модель расшифровки»?', text: 'Своя правка пропадёт.', confirmLabel: 'Вернуть', tone: 'primary' }))) return;
    setModel('');
    void write('', 'Вернули умолчание.');
  };
  // Вп8: the same shape as a field of «Профиль» — a card with the field, its hint and the actions.
  return (
    <div className="jui-stack bug-narrow">
      <form onSubmit={save} className="jui-stack bug-card bug-profile-field" aria-label="Модель расшифровки">
        <Select label="Модель расшифровки" value={model} onChange={setModel} disabled={busy} options={options} />
        <Hint>
          Без своей правки берётся «{t.model}». Слова, которые люди говорят вслух, — в «Профиль → Словарь для голоса».
          {t.unknown ? ' Сохранённой модели больше нет в списке: выберите другую.' : ''}
        </Hint>
        <FormActions
          busy={busy}
          canSave={model !== saved}
          canReset={t.custom}
          dirty={model !== saved}
          onReset={() => void reset()}
          onCancel={() => setModel(saved)}
          notice={notice}
          mark={<EditMark custom={t.custom} by={t.updatedBy} at={t.updatedAt} />}
        />
      </form>
    </div>
  );
}

const ACCESS_OPTIONS: SelectOption[] = [
  { value: 'read', label: 'Только чтение' },
  { value: 'write', label: 'Чтение и запись', hint: 'статус, заметки' },
];
const DAYS_OPTIONS: SelectOption[] = [
  { value: '30', label: '30 дней' },
  { value: '90', label: '90 дней' },
  { value: '365', label: '365 дней' },
  { value: '0', label: 'Без срока', hint: 'не рекомендуется' },
];

type Level = 'none' | 'read' | 'write';
const LEVEL_OF = (value: string): Level => (value === 'write' ? 'write' : value === 'read' ? 'read' : 'none');
const RIGHT_WORD = { read: 'чтение', write: 'запись' } as const;

/** Ра4: the projects of a token and the right on each — in the row, so it is clear where the token reaches. */
function GrantList({ grants }: { grants: { project: string; access: 'read' | 'write' }[] }) {
  return (
    <span className="bug-cell-2 bug-grants">
      {grants.map((grant) => (
        <span key={grant.project} className="jui-row bug-cell">
          <span className="mono bug-cell-name" title={grant.project}>{grant.project}</span>
          <Badge tone={grant.access === 'read' ? 'neutral' : 'accent'}>{RIGHT_WORD[grant.access]}</Badge>
        </span>
      ))}
    </span>
  );
}

/**
 * Ра4: one project of a token — «нет доступа», read, or read and write. The first (home) project of a new token has no «нет»:
 * the token is made for it.
 */
function GrantPick({
  project,
  value,
  onChange,
  disabled,
  home,
  hint,
}: {
  project: string;
  value: Level;
  onChange: (level: Level) => void;
  disabled?: boolean;
  home?: boolean;
  hint?: string;
}) {
  return (
    <div className="bug-grant-row">
      <span className="bug-grant-name" title={project}>
        {project}
        {hint && <span className="jui-muted"> {hint}</span>}
      </span>
      <Select
        label={project}
        hideLabel
        size="sm"
        value={value}
        onChange={(next) => onChange(LEVEL_OF(next))}
        disabled={disabled}
        options={home ? GRANT_OPTIONS.filter((option) => option.value !== 'none') : GRANT_OPTIONS}
      />
    </div>
  );
}

/**
 * «Доступ MCP» — tokens that reach a project (Ра4). `scope` project: the tokens of the working project, issued with a choice
 * of projects within the issuer's rights; `all` (the operator's registry tab): every token of every project.
 */
function McpAccess({
  settings,
  onError,
  scope = 'project',
}: {
  settings: BugSettings;
  onError: (err: unknown, fallback: string) => void;
  scope?: 'project' | 'all';
}) {
  const ask = useAsk();
  const [tokens, setTokens] = useState<BugToken[] | null>(null);
  /** The projects the person may put on a token (Ра4); the working project first. */
  const [reach, setReach] = useState<string[]>([settings.project]);
  const [name, setName] = useState('');
  const [issued, setIssued] = useState<{
    token: string;
    connects: string[];
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(90);
  const [rights, setRights] = useState<Record<string, Level>>({});
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<BugToken | null>(null);
  const [edited, setEdited] = useState<Record<string, Level>>({});
  const home = scope === 'project' ? settings.project : null;

  useEffect(() => {
    if (scope === 'all') {
      bugApi
        .registryTokens()
        .then((res) => setTokens(res.tokens))
        .catch((err) => onError(err, 'Не удалось загрузить токены'));
      bugApi
        .registry()
        .then((res) => setReach(res.projects.filter((project) => !project.revokedAt).map((project) => project.name)))
        .catch(() => undefined);
      return;
    }
    bugApi
      .tokens()
      .then((res) => {
        setTokens(res.tokens);
        if (res.reach?.length) setReach(res.reach);
      })
      .catch((err) => onError(err, 'Не удалось загрузить токены'));
  }, [onError, scope]);

  // Тк3: a new token opens on every project of the issuer's reach (read by default); Тк4: one choice sets them all.
  const openForm = () => {
    setName('');
    setRights(Object.fromEntries(reach.map((project) => [project, 'read' as Level])));
    setFormOpen(true);
  };
  const setAllRights = (level: Level) => setRights(Object.fromEntries(reach.map((project) => [project, level])));

  // The MCP of the project in the tracker (tracker_mcp Мс1); an older service — the host's address.
  const bases = getBases();
  const url = settings.mcp?.url ?? `${bases.bugs}/mcp`;
  const serverName = settings.mcp?.serverName ?? (bases.host ? `jaiora-bugs${bases.host.replace(/\W+/g, '-')}` : 'jaiora-bugs');

  const chosen = reach.filter((project) => (rights[project] ?? 'none') !== 'none');
  const issue = async (e: FormEvent) => {
    e.preventDefault();
    if (!chosen.length) return;
    const grants = chosen.map((project) => ({ project, access: rights[project] as 'read' | 'write' }));
    setBusy(true);
    try {
      const options = { expiresInDays: days, access: grants[0].access, grants };
      const res = scope === 'all' ? await bugApi.issueRegistryToken(name.trim(), { ...options, access: grants[0].access }) : await bugApi.issueToken(name.trim(), options);
      setTokens(res.tokens);
      setIssued({
        token: res.token,
        connects: res.connects?.length
          ? res.connects.map((line) => line.connect)
          : [res.connect ?? `claude mcp add --transport http ${serverName} ${url} --header "Authorization: Bearer ${res.token}"`],
      });
      setName('');
      setFormOpen(false);
    } catch (err) {
      onError(err, 'Не удалось выпустить токен');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (token: BugToken) => {
    if (
      !(await ask({
        title: `Отозвать токен «${token.name}»?`,
        text: 'Агент с ним перестанет видеть репорты — во всех проектах токена.',
        confirmLabel: 'Отозвать',
      }))
    )
      return;
    try {
      setTokens((scope === 'all' ? await bugApi.revokeRegistryToken(token.id) : await bugApi.revokeToken(token.id)).tokens);
    } catch (err) {
      onError(err, 'Не удалось отозвать токен');
    }
  };

  // Ра4: «Проекты токена» — the set of an existing token; only the projects in the reach change, the others stay as they are.
  const editProjects = (token: BugToken) => {
    const levels: Record<string, Level> = {};
    for (const grant of token.grants ?? []) levels[grant.project] = grant.access;
    setEdited(levels);
    setEditing(token);
  };
  const confirmProjects = async () => {
    const token = editing;
    if (!token) return;
    setEditing(null);
    const grants = reach.filter((project) => (edited[project] ?? 'none') !== 'none').map((project) => ({ project, access: edited[project] as 'read' | 'write' }));
    try {
      setTokens((scope === 'all' ? await bugApi.setRegistryTokenGrants(token.id, grants) : await bugApi.setTokenGrants(token.id, grants)).tokens);
    } catch (err) {
      onError(err, 'Не удалось изменить проекты токена');
    }
  };
  const foreign = (editing?.grants ?? []).filter((grant) => !reach.includes(grant.project));

  const columns: Column<BugToken>[] = [
    {
      key: 'name',
      width: '24%',
      header: 'Название',
      value: (token) => token.name,
      sortable: true,
      render: (token) => (
        <span className="bug-cell-2">
          <b>{token.name}</b>
          <span className="jui-muted mono">{token.prefix}…</span>
        </span>
      ),
    },
    {
      key: 'projects',
      width: '28%',
      header: 'Проекты и право',
      value: (token) => (token.grants ?? [{ project: token.projectScope ?? settings.project }]).map((grant) => grant.project).join(' '),
      sortable: true,
      render: (token) => <GrantList grants={token.grants ?? [{ project: token.projectScope ?? settings.project, access: token.access }]} />,
    },
    {
      key: 'expires',
      width: '16%',
      header: 'Срок',
      value: (token) => (token.expiresAt ? new Date(token.expiresAt) : null),
      sortable: true,
      render: (token) => {
        const expired = !!token.expiresAt && new Date(token.expiresAt).getTime() < Date.now();
        return token.expiresAt ? (
          <span className="jui-row bug-cell">
            {expired ? <Badge tone="error">истёк</Badge> : <span className="jui-muted">до</span>}
            <span className="jui-muted nowrap">{formatDateTime(token.expiresAt)}</span>
          </span>
        ) : (
          <span className="jui-muted">без срока</span>
        );
      },
    },
    {
      key: 'createdAt',
      width: '14%',
      header: 'Выпущен',
      value: (token) => new Date(token.createdAt),
      sortable: true,
      render: (token) => (
        <span className="bug-cell-2 jui-muted">
          <span className="nowrap">{formatDateTime(token.createdAt)}</span>
          {token.createdBy && <span>{token.createdBy}</span>}
        </span>
      ),
    },
    {
      key: 'lastUsedAt',
      width: '12%',
      header: 'Использован',
      value: (token) => (token.lastUsedAt ? new Date(token.lastUsedAt) : null),
      sortable: true,
      render: (token) => <span className="jui-muted nowrap">{formatDateTime(token.lastUsedAt)}</span>,
    },
    {
      key: 'actions',
      width: 110,
      header: '',
      headerLabel: 'Действия',
      align: 'right',
      sortable: false,
      filterable: false,
      render: (token) =>
        token.revokedAt ? (
          <Badge tone="error">отозван</Badge>
        ) : (
          <RowMenu
            label={`Действия: ${token.name}`}
            actions={[
              { label: 'Проекты токена', run: () => editProjects(token) },
              { label: 'Отозвать', run: () => void revoke(token) },
            ]}
          />
        ),
    },
  ];

  return (
    <div className="jui-stack">
      {/* Ра2, Ра7: the action stands in the header of the page; a drawer holds the form. */}
      <HeaderActions>
        <Button variant="primary" size="sm" icon={<PlusIcon />} onClick={openForm}>
          Выпустить токен
        </Button>
      </HeaderActions>
      <Toolbar dense>
        {scope === 'project' ? (
          <>
            <CopyValue value={url} shown={`POST ${url}`} tip="Репорты, снимки, вложения, статус и заметки — для агента" />
            <span className="jui-muted" title="Токен один; на адресе каждого проекта действует его право на этот проект. Данных самого приложения не открывает">
              Токен один: в строке — на какие проекты он действует и с каким правом
            </span>
          </>
        ) : (
          <span className="jui-muted">Все токены всех проектов. Адрес у каждого проекта свой: {url.replace(/\/mcp\/[^/]*$/, '/mcp')}/&lt;проект&gt;</span>
        )}
      </Toolbar>

      {issued && (
        <SecretBlock
          title="Токен выпущен"
          note="скопируйте сейчас — второй раз он не покажется"
          actions={
            <Button size="sm" variant="quiet" onClick={() => setIssued(null)}>
              Скрыть
            </Button>
          }
        >
          <div className="token-value mono">{issued.token}</div>
          {/* Тф3: a bounded window, so the table below stays in place whatever the number of projects. */}
          <div className="token-connects jui-stack" role="group" aria-label="Подключение">
            {issued.connects.map((line) => (
              <div className="jui-field" key={line}>
                <span className="jui-label">Подключение</span>
                <div className="token-value mono">{line}</div>
              </div>
            ))}
          </div>
        </SecretBlock>
      )}

      {tokens === null ? (
        <Loading />
      ) : tokens.length > 0 ? (
        <DataTable className="bug-fixed-table bug-cards bug-cards-tokens" label="Токены" columns={columns} rows={tokens} rowKey={(token) => token.id} pageSize={0} filter={{ placeholder: 'Название, проект' }} />
      ) : (
        <EmptyState compact>Токенов нет. «Выпустить токен» — агент получит доступ к выбранным проектам.</EmptyState>
      )}

      <Drawer
        open={formOpen}
        onClose={() => setFormOpen(false)}
        size="sm"
        title="Новый токен"
        footer={
          <Button type="submit" form="bug-token-form" variant="primary" disabled={busy || !name.trim() || !chosen.length}>
            Выпустить
          </Button>
        }
      >
        <form id="bug-token-form" onSubmit={issue} className="jui-stack bug-form bug-token-form">
          <TextField
            label="Название"
            hint="Кто и зачем им ходит"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Например: Claude Александра"
            maxLength={64}
            required
          />
          <Select label="Срок" value={String(days)} onChange={(value) => setDays(Number(value))} disabled={busy} options={DAYS_OPTIONS} />
          <Hint>Токен один, права — по проектам: что выбрано ниже, то он и открывает; на адресе каждого проекта действует его право.</Hint>
          {reach.length > 1 && (
            <div className="bug-grant-row">
              <span className="bug-grant-name">Для всех проектов</span>
              <Select
                label="Для всех проектов"
                hideLabel
                size="sm"
                value={reach.every((project) => rights[project] === rights[reach[0]]) ? (rights[reach[0]] ?? 'none') : ''}
                placeholder="Свои права по проектам"
                onChange={(next) => setAllRights(LEVEL_OF(next))}
                disabled={busy}
                options={GRANT_OPTIONS}
              />
            </div>
          )}
          <div className="bug-grant-list" role="group" aria-label="Проекты токена">
            {reach.map((project) => (
              <GrantPick
                key={project}
                project={project}
                home={project === home}
                value={rights[project] ?? 'none'}
                onChange={(level) => setRights((prev) => ({ ...prev, [project]: level }))}
                disabled={busy}
              />
            ))}
          </div>
        </form>
      </Drawer>

      <ConfirmDialog
        open={editing !== null}
        title={`Проекты токена «${editing?.name ?? ''}»`}
        tone="primary"
        confirmLabel="Сохранить"
        onConfirm={() => void confirmProjects()}
        onCancel={() => setEditing(null)}
      >
        <div className="bug-form">
          <Hint>Проекты, на которые токен открывает доступ, и право на каждый. «нет доступа» — токен проект больше не видит.</Hint>
          <div className="bug-grant-list" role="group" aria-label="Проекты токена">
            {reach.map((project) => (
              <GrantPick key={project} project={project} value={edited[project] ?? 'none'} onChange={(level) => setEdited((prev) => ({ ...prev, [project]: level }))} />
            ))}
          </div>
          {foreign.length > 0 && (
            <Hint>Ещё на этом токене: {foreign.map((grant) => `${grant.project} (${RIGHT_WORD[grant.access]})`).join(', ')} — вне ваших прав, остаются как есть.</Hint>
          )}
        </div>
      </ConfirmDialog>
    </div>
  );
}

/**
 * «Проекты»: hosts registered in the service — only for a super of a project that reads all (facts prod). A new project
 * or a new key is shown once, with the lines for the host's .env; the own project's key is changed only from the CLI.
 */
function ProjectRegistry({ onError, selected }: { onError: Failed; selected?: RegistrySelection }) {
  const ask = useAsk();
  const [projects, setProjects] = useState<RegistryProject[] | null>(null);
  const [name, setName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  // Мд2: rights of the token on the other projects (none — not reached).
  const [tokenGrants, setTokenGrants] = useState<Record<string, 'none' | 'read' | 'write'>>({});
  const [issued, setIssued] = useState<IssuedProjectKey | null>(null);
  const [issuedToken, setIssuedToken] = useState<IssuedProjectToken | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  /** Пл2: the project of the clicked row, shown in a panel over the list. */
  const [detailName, setDetailName] = useState<string | null>(null);

  useEffect(() => {
    bugApi
      .registry()
      .then((res) => setProjects(res.projects))
      .catch((err) => onError(err, 'Не удалось загрузить проекты'));
  }, [onError]);

  const run = async (action: () => Promise<{ projects: RegistryProject[] } & Partial<IssuedProjectKey>>, fallback: string) => {
    setBusy(true);
    try {
      const res = await action();
      setProjects(res.projects);
      if (res.key && res.env && res.name) {
        setIssued({
          name: res.name,
          key: res.key,
          env: res.env,
          projects: res.projects,
          mcp: res.mcp,
          owner: res.owner,
        });
        setIssuedToken(null);
        setCopied(false);
      }
      return true;
    } catch (err) {
      onError(err, fallback);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const create = async (e: FormEvent) => {
    e.preventDefault();
    const owner = { username: ownerName.trim(), ...(ownerPassword ? { password: ownerPassword } : {}) };
    if (await run(() => bugApi.registerProject(name.trim(), owner), 'Не удалось создать проект')) {
      setName('');
      setOwnerName('');
      setOwnerPassword('');
      setNewOpen(false);
    }
  };
  const rotate = async (project: RegistryProject) => {
    const yes = await ask({
      title: `Новый ключ для «${project.name}»?`,
      text: 'Это ключ хоста проекта: пароль, по которому хост отправляет репорты в сервис. Прежний перестанет работать сразу — хост получит 401, пока в его .env не встанет новый. Новый покажется один раз.',
      confirmLabel: 'Новый ключ',
    });
    if (yes) void run(() => bugApi.rotateProject(project.name), 'Не удалось сменить ключ');
  };
  // tracker_mcp Мс3, Пр6: a token for the project's own MCP, issued in that project (listed in its tracker settings);
  // the registrar picks the rights and the term in the dialog (read by default), the name is «Агент <проект>».
  const [tokenFor, setTokenFor] = useState<RegistryProject | null>(null);
  const [tokenName, setTokenName] = useState('');
  const [tokenAccess, setTokenAccess] = useState<'read' | 'write'>('read');
  const [tokenDays, setTokenDays] = useState(90);
  const issueToken = (project: RegistryProject) => {
    setTokenName(`Агент ${project.name}`);
    setTokenAccess('read');
    setTokenDays(90);
    setTokenGrants({});
    setTokenFor(project);
  };
  const confirmToken = async () => {
    const project = tokenFor;
    if (!project || !tokenName.trim()) return;
    setTokenFor(null);
    setBusy(true);
    try {
      const res = await bugApi.issueProjectToken(project.name, {
        name: tokenName.trim(),
        access: tokenAccess,
        expiresInDays: tokenDays,
        grants: [
          { project: project.name, access: tokenAccess },
          ...Object.entries(tokenGrants)
            .filter(([other, level]) => other !== project.name && level !== 'none')
            .map(([other, level]) => ({ project: other, access: level as 'read' | 'write' })),
        ],
      });
      setProjects(res.projects);
      setIssued(null);
      setIssuedToken(res);
      setCopied(false);
    } catch (err) {
      onError(err, 'Не удалось выпустить токен');
    } finally {
      setBusy(false);
    }
  };
  const revoke = async (project: RegistryProject) => {
    const yes = await ask({
      title: `Отозвать ключ «${project.name}»?`,
      text: 'Хост перестанет отправлять и видеть репорты; репорты и токены остаются, вернуть — «Новый ключ».',
      confirmLabel: 'Отозвать',
    });
    if (yes) void run(() => bugApi.revokeProject(project.name), 'Не удалось отозвать ключ');
  };

  const most = Math.max(1, ...(projects ?? []).map((project) => project.reports));
  const columns: Column<RegistryProject>[] = [
    {
      key: 'name',
      width: '24%',
      header: 'Проект',
      value: (project) => project.name,
      sortable: true,
      render: (project) => (
        <span className={`bug-cell-2${project.revokedAt ? ' bug-revoked' : ''}`}>
          <span className="jui-row bug-cell">
            <b className="bug-cell-name" title={project.name}>{project.name}</b>
            {/* Т9: the mark stays in sight, not in the actions column off the edge. */}
            {project.revokedAt && <Badge tone="error">отозван</Badge>}
            {project.own && <Badge>этот</Badge>}
            {project.readsAll && <Badge tone="accent">видит все</Badge>}
          </span>
          <span className="jui-muted mono">{project.keyPrefix}…</span>
        </span>
      ),
    },
    {
      key: 'mcp',
      width: '34%',
      header: 'MCP',
      value: (project) => project.mcp?.url ?? '',
      sortable: true,
      render: (project) =>
        project.mcp ? (
          <CopyValue value={project.mcp.url} tip="Адрес MCP проекта в трекере: только его репорты, токен — только его" />
        ) : (
          <span className="jui-muted">—</span>
        ),
    },
    {
      key: 'reports',
      width: '10%',
      header: 'Репорты',
      value: (project) => project.reports,
      sortable: true,
      render: (project) => <MiniBar value={project.reports} max={most} label="Репортов" />,
    },
    {
      key: 'createdAt',
      width: '18%',
      header: 'Создан',
      value: (project) => new Date(project.createdAt),
      sortable: true,
      render: (project) => <span className="jui-muted nowrap">{formatDateTime(project.createdAt)}</span>,
    },
    {
      key: 'actions',
      width: 110,
      header: '',
      headerLabel: 'Действия',
      align: 'right',
      sortable: false,
      filterable: false,
      // Т14: «Отозвать ключ» is always in the menu; where it cannot be used it is off with the reason.
      render: (project) => (
        <span className="jui-row bug-actions">
          {!selected && project.own && <Badge tip="Ключ этого хоста меняется только командой project на сервере">только CLI</Badge>}
          <RowMenu
            label={`Действия: ${project.name}`}
            actions={selected ? [
              // Ра1, Ра3, Ра6: the project's own things live in the project — the row leads there; keys are not changed from the list.
              { label: 'Открыть проект', run: () => selected.openProject(project.name, 'profile') },
              { label: 'Пользователи', run: () => selected.openProject(project.name, 'users') },
              { label: 'Токены MCP', run: () => selected.openProject(project.name, 'mcp') },
              { label: 'Ключ хоста', run: () => selected.openProject(project.name, 'host') },
            ] : [
              {
                label: 'Токен MCP',
                disabled: busy,
                run: () => void issueToken(project),
              },
              {
                label: 'Новый ключ',
                disabled: busy || project.own,
                ...(project.own ? { hint: 'свой ключ — только командой project на сервере' } : {}),
                run: () => void rotate(project),
              },
              {
                label: project.revokedAt ? `Отозван ${formatDateTime(project.revokedAt)}` : 'Отозвать ключ',
                disabled: busy || project.own || !!project.revokedAt,
                ...(revokeOff(project) ? { hint: revokeOff(project)! } : {}),
                run: () => void revoke(project),
              },
            ]}
          />
        </span>
      ),
    },
  ];

  const newFields = (
    <>
      <TextField
        className="bug-grow"
        label="Название проекта"
        hint="Латиница, цифры, дефис. Из него адрес MCP; потом не меняется."
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="например: city-board"
        pattern="[a-z0-9][a-z0-9._\-]{0,63}"
        maxLength={64}
        required
      />
      <TextField
        className="bug-grow"
        label="Владелец"
        hint="Логин суперадмина проекта."
        value={ownerName}
        onChange={(e) => setOwnerName(e.target.value)}
        autoComplete="off"
        maxLength={64}
        required
      />
      <TextField
        className="bug-grow"
        label="Пароль владельца"
        hint="Пусто — выдадим сами."
        type="password"
        value={ownerPassword}
        onChange={(e) => setOwnerPassword(e.target.value)}
        autoComplete="new-password"
      />
    </>
  );

  const detail = selected && detailName ? projects?.find((project) => project.name === detailName) : undefined;
  return (
    <div className="jui-stack">
      {issued && (
        <SecretBlock
          title={`Ключ хоста «${issued.name}»`}
          note="Показывается один раз: скопируйте сейчас, потом его не увидеть — только выпустить новый."
          actions={
            <>
              <Button size="sm" variant="primary" onClick={() => copyText(issued.env.join('\n'), setCopied)}>
                {copied ? 'Скопировано' : 'Копировать строки'}
              </Button>
              <Button size="sm" variant="quiet" onClick={() => setIssued(null)}>
                Скрыть
              </Button>
            </>
          }
        >
          <div className="jui-field">
            <span className="jui-label">Что это · пароль хоста «{issued.name}» к сервису репортов</span>
            <CopyValue value={issued.key} />
          </div>
          <div className="jui-field">
            <span className="jui-label">Куда вставить · строки в .env хоста, затем перезапустить хост</span>
            <div className="jui-stack">
              {issued.env.map((line) => (
                <CopyValue key={line} value={line} />
              ))}
            </div>
          </div>
          {issued.owner && (
            <div className="jui-field">
              <span className="jui-label">Владелец проекта · логин {issued.owner.username}, суперадмин «{issued.name}»</span>
              {issued.owner.password ? <CopyValue value={issued.owner.password} /> : <span className="jui-muted">пароль — тот, что вы ввели</span>}
            </div>
          )}
          {issued.mcp && (
            <div className="jui-field">
              <span className="jui-label">MCP проекта · токен — «Токен MCP» в строке проекта</span>
              <div className="token-value mono">{issued.mcp.url}</div>
            </div>
          )}
        </SecretBlock>
      )}

      <ConfirmDialog
        open={tokenFor !== null}
        title={`Токен MCP для «${tokenFor?.name ?? ''}»`}
        tone="primary"
        confirmLabel="Выпустить"
        onConfirm={() => void confirmToken()}
        onCancel={() => setTokenFor(null)}
      >
        <div className="bug-form">
          <TextField label="Имя" value={tokenName} onChange={(e) => setTokenName(e.target.value)} maxLength={64} required />
          <Select
            label="Права"
            value={tokenAccess}
            onChange={(value) => setTokenAccess(value === 'write' ? 'write' : 'read')}
            options={ACCESS_OPTIONS}
          />
          <Select label="Срок" value={String(tokenDays)} onChange={(value) => setTokenDays(Number(value))} options={DAYS_OPTIONS} />
          {/* Мд2: one token, a set of projects, a right on each; the address of every project stays its own. */}
          {(projects ?? []).filter((other) => other.name !== tokenFor?.name && !other.revokedAt).length > 0 && (
            <Hint>Токен один, права — по проектам: что выбрано ниже, то он и открывает; на адресе каждого проекта действует его право.</Hint>
          )}
          {(projects ?? [])
            .filter((other) => other.name !== tokenFor?.name && !other.revokedAt)
            .map((other) => (
              <Select
                key={other.name}
                label={`Ещё и «${other.name}»`}
                value={tokenGrants[other.name] ?? 'none'}
                onChange={(value) => setTokenGrants((prev) => ({ ...prev, [other.name]: value === 'write' ? 'write' : value === 'read' ? 'read' : 'none' }))}
                options={GRANT_OPTIONS}
              />
            ))}
        </div>
      </ConfirmDialog>

      {issuedToken && (
        <SecretBlock
          title={`Токен MCP «${issuedToken.project}»`}
          note="скопируйте сейчас, второй раз он не покажется"
          actions={
            <>
              <Button size="sm" variant="primary" onClick={() => copyText((issuedToken.mcps?.length ? issuedToken.mcps : [issuedToken.mcp]).map((line) => line.connect).join('\n'), setCopied)}>
                {copied ? 'Скопировано' : 'Копировать подключение'}
              </Button>
              <Button size="sm" variant="quiet" onClick={() => setIssuedToken(null)}>
                Скрыть
              </Button>
            </>
          }
        >
          <div className="token-value mono">{issuedToken.token}</div>
          {(issuedToken.mcps?.length ? issuedToken.mcps : [issuedToken.mcp]).map((line) => (
            <div className="jui-field" key={line.serverName}>
              <span className="jui-label">Подключение · {line.serverName}</span>
              <div className="token-value mono">{line.connect}</div>
            </div>
          ))}
        </SecretBlock>
      )}

      {projects === null ? (
        <Loading />
      ) : (
        <DataTable
          className="bug-fixed-table bug-cards bug-cards-projects"
          label="Проекты"
          columns={columns}
          rows={projects}
          rowKey={(project) => project.name}
          pageSize={0}
          filter={{ placeholder: 'Проект, адрес MCP' }}
          {...(selected ? { onRowClick: (project: RegistryProject) => setDetailName(project.name) } : {})}
        />
      )}
      {selected && (
        <Drawer open={!!detail} onClose={() => setDetailName(null)} size="md" title={detail ? `Проект · ${detail.name}` : 'Проект'}>
          {detail && <ProjectCard project={detail} selection={selected} />}
        </Drawer>
      )}

      {/* Мд3: a project is made with its owner; the name is the project's address (277). */}
      {selected ? (
        <>
          {/* Ра2: «Новый проект» — one button, in the header of the registry; the form is a drawer, not a row under the table. */}
          <HeaderActions>
            <Button variant="primary" size="sm" icon={<PlusIcon />} onClick={() => setNewOpen(true)}>
              Новый проект
            </Button>
          </HeaderActions>
          <Drawer
            open={newOpen}
            onClose={() => setNewOpen(false)}
            size="sm"
            title="Новый проект"
            footer={
              <Button type="submit" form="bug-project-form" variant="primary" disabled={busy || !name.trim() || !ownerName.trim()}>
                Создать
              </Button>
            }
          >
            <form id="bug-project-form" onSubmit={create} className="jui-stack bug-form">
              {newFields}
            </form>
          </Drawer>
        </>
      ) : (
        <form onSubmit={create} className="jui-row bug-form">
          {newFields}
          <Button type="submit" variant="primary" disabled={busy || !name.trim() || !ownerName.trim()}>
            Создать
          </Button>
        </form>
      )}
    </div>
  );
}

type Moved = (project: string, from: BugStatus, to: BugStatus | null) => void;

export type ReportsView = 'board' | 'backlog' | 'all' | 'list' | 'epics' | 'epicBacklog';
const VIEW_OF_SECTION = { backlog: 'backlog', board: 'board', taskList: 'list', epics: 'epics', epicBacklog: 'epicBacklog', allReports: 'all' } as const;

/** Мн2: the list of «Задачи» — all reports, and «Бэклог» as the stage filter above them (the first stage, not a section). */
function TaskList({
  stage: initial,
  onError,
  onMoved,
  renderDetail,
}: {
  stage?: 'backlog';
  onError: (err: unknown, fallback: string) => void;
  onMoved?: Moved;
  renderDetail: (id: number, onChanged: (report: BugReportDetail) => void, onDeleted: () => void) => ReactNode;
}) {
  const [stage, setStage] = useState<'all' | 'backlog'>(initial ?? 'all');
  return (
    <div className="jui-stack">
      <Segmented
        label="Этап"
        value={stage}
        onChange={(value) => setStage(value as 'all' | 'backlog')}
        options={[
          { value: 'all', label: 'Все этапы' },
          { value: 'backlog', label: 'Бэклог' },
        ]}
      />
      {stage === 'backlog' ? (
        <BacklogPage onError={onError} onMoved={onMoved} renderDetail={renderDetail} />
      ) : (
        <AllReports onError={onError} onMoved={onMoved} renderDetail={renderDetail} />
      )}
    </div>
  );
}

function ReportView({
  view,
  stage,
  onError,
  onMoved,
}: {
  view: ReportsView;
  stage?: 'backlog';
  onError: (err: unknown, fallback: string) => void;
  onMoved?: Moved;
}) {
  const detail = (id: number, onChanged: (report: BugReportDetail) => void, onDeleted: () => void) => (
    <ReportDetail key={id} id={id} onChanged={onChanged} onDeleted={onDeleted} onError={onError} />
  );
  if (view === 'epics') return <EpicKanban onError={onError} />;
  if (view === 'epicBacklog') return <EpicList onError={onError} part="backlog" />;
  if (view === 'backlog') return <BacklogPage onError={onError} onMoved={onMoved} renderDetail={detail} />;
  if (view === 'list') return <TaskList stage={stage} onError={onError} onMoved={onMoved} renderDetail={detail} />;
  if (view === 'all') return <AllReports onError={onError} onMoved={onMoved} renderDetail={detail} />;
  return <ReportBoard onError={onError} onMoved={onMoved} renderDetail={detail} />;
}

/** Пт8: a host (`mountSettings` without `tracker`) keeps no reports of its own — a way to the tracker site instead. */
function TrackerLink({ url }: { url: string | undefined }) {
  return (
    <section className="jui-panel bug-tracker-link" aria-label="Трекер">
      <span className="bug-tracker-link-icon" aria-hidden>
        <BugIcon />
      </span>
      <strong className="bug-grow">Репорты, доска и эпики — на сайте трекера</strong>
      {url && (
        <ButtonLink variant="primary" href={`${url}/#board`} target="_blank" rel="noopener noreferrer">
          Открыть трекер
        </ButtonLink>
      )}
    </section>
  );
}

const placeOf = (question: StoredQuestion): string => {
  if (question.places.length === 0) return 'экран';
  if (question.places.length > 1) return `${question.places.length} места`;
  const [place] = question.places;
  return place.kind === 'screen' ? 'весь экран' : place.text || place.anchors[0] || 'место';
};

const QUESTION_COLUMNS: Column<StoredQuestion>[] = [
  { key: 'id', header: '№', width: 70, value: (q) => q.id, render: (q) => <span className="mono nowrap" data-bug-open="">№{q.id}</span> },
  { key: 'question', header: 'Вопрос', minWidth: 220, value: (q) => q.question, render: (q) => <span data-pii="">{q.question}</span> },
  {
    key: 'found',
    header: 'В коде',
    width: 120,
    value: (q) => (q.found ? 1 : 0),
    render: (q) => <span className={`bug-status-dot bug-status-${q.found ? 'success' : 'neutral'}`}>{q.found ? 'есть в коде' : 'не найдено'}</span>,
  },
  { key: 'report', header: 'Репорт', width: 100, value: (q) => (q.reportId ? (q.reportNumber ?? q.reportId) : null), render: (q) => <span className="bug-question-report nowrap">{q.reportId ? `→ №${q.reportNumber ?? q.reportId}` : ''}</span> },
  { key: 'asker', header: 'Автор', width: 130, value: (q) => q.asker ?? '', render: (q) => <span data-pii="">{q.asker ?? '—'}</span> },
  { key: 'project', header: 'Проект', width: 130, value: (q) => q.project, render: (q) => <span data-pii="">{q.project}</span> },
  { key: 'date', header: 'Дата', width: 120, value: (q) => new Date(q.createdAt), render: (q) => <span className="nowrap" data-pii="">{dayOf(q.createdAt)}</span> },
];

/** «Задать вопрос»: answered questions stored by the widget; a question made into a report shows its number. */
export function QuestionList({
  onError,
  onCount,
  untitled,
}: {
  onError: (err: unknown, fallback: string) => void;
  onCount?: (count: QuestionCount) => void;
  /** The page header already names the section (tracker site). */
  untitled?: boolean;
}) {
  const ask = useAsk();
  const [questions, setQuestions] = useState<StoredQuestion[] | null>(null);
  const [next, setNext] = useState<number | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [mine, setMine] = useState(false);
  const opened = questions?.find((item) => item.id === openId);

  const load = useCallback(
    async (before: number | null) => {
      try {
        const res = await bugApi.questions(before);
        setQuestions((prev) => (before ? [...(prev ?? []), ...res.questions] : res.questions));
        setNext(res.nextBefore);
        setMine(res.mine === true);
      } catch (err) {
        onError(err, 'Не удалось загрузить вопросы');
      }
    },
    [onError],
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  useEffect(() => {
    if (questions) onCount?.({ count: questions.length, more: !!next });
  }, [questions, next, onCount]);

  const remove = async (id: number) => {
    if (!(await ask({ title: `Удалить вопрос №${id}?` }))) return;
    try {
      await bugApi.removeQuestion(id);
      setQuestions((prev) => (prev ?? []).filter((item) => item.id !== id));
    } catch (err) {
      onError(err, 'Не удалось удалить вопрос');
    }
  };

  return (
    <Panel title={untitled ? undefined : mine ? 'Мои вопросы' : 'Вопросы'} label={untitled ? (mine ? 'Мои вопросы' : 'Вопросы') : undefined} headingLevel={3}>
      {questions === null ? (
        <Loading />
      ) : questions.length === 0 ? (
        <EmptyState compact>Вопросов нет</EmptyState>
      ) : (
        <>
          {/* Ср2: the questions are a DataTable like every table of the tracker — every column sorts, the row opens below. */}
          <DataTable
            className="bug-fixed-table bug-questions-table"
            label="Вопросы"
            columns={QUESTION_COLUMNS}
            rows={questions}
            rowKey={(item) => item.id}
            pageSize={0}
            filter={{ placeholder: 'Вопрос, автор, проект' }}
            rowTone={(item) => (item.id === openId ? 'selected' : undefined)}
            onRowClick={(item) => setOpenId(openId === item.id ? null : item.id)}
          />
          {opened && (
            <div className="jui-stack bug-report-detail bug-question-detail" data-question={opened.id}>
              <Toolbar dense>
                <span data-pii="">
                  <Badge>{placeOf(opened)}</Badge>
                </span>
                {opened.route && (
                  <span className="jui-muted mono" data-pii="">
                    {opened.route}
                  </span>
                )}
              </Toolbar>
              <p className="bug-pre" data-pii="">
                {opened.found ? opened.answer : `Не нашёл в коде: ${opened.guess ?? 'нет'}`}
              </p>
              {opened.proofs.length > 0 && (
                <div className="jui-muted mono" data-pii="">
                  {opened.proofs.map((proof) => `${proof.file}:${proof.line}`).join(' · ')}
                </div>
              )}
              {!mine && (
                <div>
                  <Button size="sm" variant="quiet" onClick={() => void remove(opened.id)}>
                    Удалить
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}
      {next && (
        <Button size="sm" variant="quiet" className="bug-more" onClick={() => void load(next)}>
          Ещё
        </Button>
      )}
    </Panel>
  );
}

function ReportDetail({
  id,
  onChanged,
  onDeleted,
  onError,
}: {
  id: number;
  onChanged: (report: BugReportDetail) => void;
  onDeleted: () => void;
  onError: (err: unknown, fallback: string) => void;
}) {
  const ask = useAsk();
  const [report, setReport] = useState<BugReportDetail | null>(null);
  const [status, setStatus] = useState<BugStatus>('backlog');
  const [note, setNote] = useState('');
  const [duplicateOf, setDuplicateOf] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    bugApi
      .get(id)
      .then((res) => {
        setReport(res.report);
        setStatus(res.report.status);
      })
      .catch((err) => onError(err, 'Не удалось открыть репорт'));
  }, [id, onError]);

  if (!report)
    return (
      <div className="bug-report-detail">
        <Loading />
      </div>
    );

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await bugApi.update(id, {
        ...(status !== report.status || status === 'duplicate' ? { status, expectedStatus: report.status } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        // Т15: «Дубль №» — a number in the report's own project.
        ...(status === 'duplicate' ? { duplicateNumber: Number(duplicateOf), duplicateProject: report.project } : {}),
      });
      setReport(res.report);
      setNote('');
      onChanged(res.report);
    } catch (err) {
      onError(err, 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (
      !(await ask({
        title: `Удалить репорт ${reportLabel(report)}?`,
        text: 'Со скриншотом, файлами и заметками — насовсем.',
      }))
    )
      return;
    setBusy(true);
    try {
      await bugApi.remove(id);
      onDeleted();
    } catch (err) {
      onError(err, 'Не удалось удалить репорт');
      setBusy(false);
    }
  };

  const shots = report.attachments.filter((a) => a.kind === 'screenshot');
  const others = report.attachments.filter((a) => a.kind !== 'screenshot');
  const changed = status !== report.status || !!note.trim();
  const shotLog = shotLogOf(report.clientState);

  return (
    <div className="jui-stack bug-report-detail">
      <RepeatBlock reportId={report.id} onError={onError} />
      {report.description && (
        <div className="bug-pre" data-pii="">
          {report.description}
        </div>
      )}
      <Toolbar dense>
        {report.pageUrl && (
          <span data-pii="">
            <CopyValue value={report.pageUrl} />
          </span>
        )}
        <Badge>версия {report.appVersion ?? '—'}</Badge>
      </Toolbar>
      {shots.map((shot) => (
        <a key={shot.id} href={attachmentUrl(shot.id)} target="_blank" rel="noreferrer">
          <img className="bug-report-shot" src={attachmentUrl(shot.id)} alt={`Скриншот к репорту ${reportLabel(report)}`} />
        </a>
      ))}
      {others.length > 0 && (
        <div className="bug-list">
          {others.map((file) =>
            file.kind === 'voice' ? (
              <div key={file.id} className="jui-stack bug-voice">
                <audio controls preload="none" src={attachmentUrl(file.id)} />
                {typeof file.meta?.transcript === 'string' && (
                  <span className="jui-muted" data-pii="">
                    «{file.meta.transcript}»
                  </span>
                )}
                <span className="jui-muted">{Math.max(1, Math.round(file.size / 1024))} КБ</span>
              </div>
            ) : (
              <Row
                key={file.id}
                title={
                  <a href={attachmentUrl(file.id, true)} data-pii="">
                    {file.kind === 'page' ? 'HTML страницы' : file.fileName}
                  </a>
                }
                meta={`${Math.max(1, Math.round(file.size / 1024))} КБ`}
              />
            ),
          )}
        </div>
      )}
      {report.notes.length > 0 && (
        <div className="bug-list">
          {report.notes.map((n, i) => (
            <Row
              key={i}
              wrap
              title={
                <>
                  {n.statusTo && (
                    <>
                      {n.statusFrom ? `${BUG_STATUS_LABELS[n.statusFrom]} → ` : ''}
                      <StatusBadge status={n.statusTo} />{' '}
                    </>
                  )}
                  <span data-pii="">{n.text}</span>
                </>
              }
              meta={
                <>
                  <span data-pii="">
                    {formatDateTime(n.at)} · {n.author ?? '—'}
                  </span>{' '}
                  ({n.channel === 'mcp' ? 'MCP' : 'админка'})
                </>
              }
            />
          ))}
        </div>
      )}
      <AgentControlBlock reportId={report.id} refresh={`${report.status}:${report.notes.length}`} />
      <ReportEpics reportId={report.id} onError={onError} />
      {shotLog.length > 0 && (
        <details className="bug-details">
          <summary>
            Журнал снимков <Badge>{shotLog.length}</Badge>
          </summary>
          <div className="bug-list">
            {shotLog.map((entry, i) => (
              <Row
                key={i}
                title={
                  <>
                    {SHOT_KINDS[entry.kind] ?? entry.kind} · <span data-pii="">{entry.page}</span>
                    {entry.area ? ` · ${entry.area.w}×${entry.area.h}` : ''}
                    {entry.masked ? ' · щит' : ''}
                  </>
                }
                meta={
                  <>
                    <Badge tone={SHOT_TONES[entry.result] ?? 'neutral'}>
                      {SHOT_RESULTS[entry.result] ?? entry.result}
                      {entry.error ? `: ${entry.error}` : ''}
                    </Badge>
                    {entry.px ? `${entry.px.w}×${entry.px.h}` : ''}
                    {typeof entry.ms === 'number' ? ` · ${entry.ms} мс` : ''}
                    {Array.isArray(entry.blocks) ? ` · блоков в кадре: ${entry.blocks.length}` : ''}
                  </>
                }
              >
                {Array.isArray(entry.blocks) && entry.blocks.length > 0 && (
                  <ul className="bug-shot-blocks">
                    {entry.blocks.map((b, k) => (
                      <li key={k}>
                        <b>{b.block}</b> <code>{b.path}</code>
                      </li>
                    ))}
                  </ul>
                )}
              </Row>
            ))}
          </div>
        </details>
      )}
      <details className="bug-details">
        <summary>Слепок браузера и сервера (JSON)</summary>
        <pre className="bug-report-json" data-pii="">{JSON.stringify({ client: report.clientState, server: report.serverState }, null, 2)}</pre>
      </details>
      <form onSubmit={save} className="jui-row bug-form">
        <Select
          className="bug-fixed"
          label="Статус"
          value={status}
          onChange={(value) => setStatus(value as BugStatus)}
          disabled={busy}
          options={STATUS_OPTIONS.filter((o) => o.value === report.status || allowedMove(report.status, o.value))}
          renderValue={() => <StatusBadge status={status} />}
        />
        {status === 'duplicate' && (
          <TextField
            className="bug-num"
            label="Дубль №"
            value={duplicateOf}
            onChange={(e) => setDuplicateOf(e.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
            disabled={busy}
          />
        )}
        <TextField
          className="bug-grow"
          label="Заметка"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={5000}
          placeholder="Что нашлось, где исправлено"
          disabled={busy}
        />
        <Button type="submit" variant="primary" disabled={busy || !changed}>
          Сохранить
        </Button>
        <Button variant="danger" disabled={busy} onClick={() => void remove()}>
          Удалить
        </Button>
      </form>
    </div>
  );
}
